import {isSafeRelativeUrl} from '@lib/url';
import {signIn} from 'next-auth/react';
import useSWRMutation from 'swr/mutation';

type Auth0SignInParams = {
  organization?: string;
  organization_name?: string;
  invitation?: string;
  login_hint?: string;
  callbackUrl?: string;
};

/**
 * The only params forwarded to Auth0's `/authorize`.
 *
 * Callers hand over whatever sits on the URL - useAuthGuard copies the entire query string
 * of the page the user was bounced from - so without an allowlist unrelated app state, and
 * anything a crafted link injects (`connection`, `prompt`, `screen_hint`), reaches the IdP.
 * The type above already describes this contract; this is what enforces it at runtime.
 */
const FORWARDED_TO_AUTH0 = [`organization`, `organization_name`, `invitation`, `login_hint`] as const;

const fetcher = (_key: string, {arg = {params: {}}}: {arg?: {params?: Auth0SignInParams}}) => {
  const params = arg?.params ?? {};

  // Values reach us already decoded, since callers read them off URLSearchParams. Decoding
  // again threw URIError on a literal percent - inside the fetcher, so it took sign-in down.
  const authParams: Record<string, string> = {};
  for (const key of FORWARDED_TO_AUTH0) {
    const value = params[key];
    if (value) authParams[key] = value;
  }

  // We avoid passing the provider as Auth0 is our only one, and it might be creating issue
  // https://github.com/nextauthjs/next-auth/issues/752#issuecomment-705040427
  const {callbackUrl} = params;
  return signIn(`auth0`, {callbackUrl: isSafeRelativeUrl(callbackUrl) ? callbackUrl : `/`}, authParams);
};

/** An async wrapper for next-auth signin, so SWR will handle the async state machine for us */
export const useSignIn = () => {
  const {data, error, trigger, isMutating, reset} = useSWRMutation(`/signin/auth0`, fetcher, {revalidate: false});
  return {
    data,
    error,
    trigger,
    isMutating,
    reset,
  };
};
